import { create } from 'zustand';
import { client } from '../graphql/client';
import {
    BATCH_GET_CONFIG_ENABLE,
    GET_MY_DATA_PORTAL_MENU,
} from '../pages/data-portal/graphql/queries';
import { getUserInfo } from '../utils/auth';
import {
    DATA_PORTAL_EMBED_ENABLE_KEY,
    MyDataPortalGroup,
    MyDataPortalPage,
    canConfigDataPortal,
    getMyDataPortalGroups,
    getMyDataPortalPages,
    getMyDataPortalPortalCodes,
    isDataPortalEnabled,
    saveDataPortalCanConfig,
    saveDataPortalEnabled,
    saveMyDataPortalGroups,
    saveMyDataPortalPages,
    saveMyDataPortalPortalCodes,
} from '../utils/dataPortalCache';

/**
 * 数据门户的菜单信号（灰度开关 + 当前用户可见看板）。
 *
 * 为什么要用 store 而不是只写 localStorage：
 * `MainLayout` 的 `getDefaultFirstMenu()` 是同步函数，只能同步读缓存；而缓存是异步写入的，
 * 菜单的 `useMemo` 又不依赖它，于是「缓存后到 → 菜单不重算」——表现为首次进来看不到
 * 「数据门户」、要刷第二次。这个坑在「页面加载」和「登录跳转」两条路径上各踩了一次，
 * 每次都得在调用点补一个 await，以后再多一条进首页的路径还会再漏。
 *
 * 放进 store 后，`refresh()` 写完自动触发订阅方重渲染，谁先谁后都不影响正确性。
 *
 * localStorage 仍然保留：store 初始值从它同步 hydrate，这样刷新后首帧就是对的，
 * 不会先渲染一个没有「数据门户」的菜单再闪一下补上。
 */
export interface DataPortalState {
    /** 灰度开关对当前用户是否开启 */
    enabled: boolean;
    /** 当前用户是否在配置白名单内（能进「专题分析配置」页） */
    canConfig: boolean;
    /** 当前用户可见的看板（后端已按可见范围过滤，配置白名单内的人不受限） */
    pages: MyDataPortalPage[];
    /** 二级栏的分组行；组内没有可见看板的分组后端不会下发 */
    groups: MyDataPortalGroup[];
    /**
     * 可见的来源门户 code，已按 sortOrder 排好序。
     *
     * **空数组 = 不展示门户分层**，二级栏按「分组 → 看板」两层渲染（本次改动前的形态）。
     * 只有配置白名单内的人拿到非空值。门户层不是授权边界，它只影响可见看板怎么摆。
     */
    portalCodes: string[];
    isLoading: boolean;
    /**
     * 全量刷新：灰度开关 + myDataPortalMenu，两个请求并行。
     * 进模块 / 登录成功 / 页面加载这三处入口用（此时灰度状态未知，必须查）。
     */
    refresh: () => Promise<void>;
    /**
     * 轻量刷新：只重拉 myDataPortalMenu，**不重查灰度**。
     * 管理页保存/删除/移动后用 —— 管理操作不会翻转灰度开关，没必要每次都多花一次慢的灰度查询。
     */
    refreshMenu: () => Promise<void>;
}

interface ConfigEnableResponse {
    batchGetConfigEnable: { results: { configKey: string; enabled: boolean }[] };
}

interface MyMenuResponse {
    myDataPortalMenu: {
        pages: MyDataPortalPage[];
        groups: MyDataPortalGroup[];
        portalCodes: string[];
        canConfig: boolean;
    };
}

/**
 * 从 localStorage 同步 hydrate，保证刷新后首帧菜单就是对的、不闪一下。
 * 包 try/catch 是防御性的：这里在模块初始化阶段跑，万一模块加载顺序让 auth 还没就绪，
 * 也只是退化成「首帧没有数据门户、拿到数据后补上」，不能让整个应用起不来。
 */
function hydrateFromCache(): Pick<
    DataPortalState,
    'enabled' | 'canConfig' | 'pages' | 'groups' | 'portalCodes'
> {
    try {
        return {
            enabled: isDataPortalEnabled(),
            canConfig: canConfigDataPortal(),
            pages: getMyDataPortalPages() ?? [],
            groups: getMyDataPortalGroups() ?? [],
            portalCodes: getMyDataPortalPortalCodes() ?? [],
        };
    } catch (e) {
        console.warn('Hydrate data portal cache failed', e);
        return { enabled: false, canConfig: false, pages: [], groups: [], portalCodes: [] };
    }
}

/**
 * 把 myDataPortalMenu 的返回落进 localStorage，并返回归一化后的四个菜单字段。
 * refresh（全量）与 refreshMenu（轻量）共用，避免两处各写一遍解析/落盘逻辑。
 * 空数组/false 即「非配置人员 / 不分层」，与后端 EMPTY_MENU 语义直接对应。
 */
function applyMenu(uid: string, menu: MyMenuResponse['myDataPortalMenu'] | undefined) {
    const pages = menu?.pages ?? [];
    const groups = menu?.groups ?? [];
    const portalCodes = menu?.portalCodes ?? [];
    const canConfig = menu?.canConfig ?? false;
    saveMyDataPortalPages(uid, pages);
    saveMyDataPortalGroups(uid, groups);
    saveMyDataPortalPortalCodes(uid, portalCodes);
    saveDataPortalCanConfig(uid, canConfig);
    return { pages, groups, portalCodes, canConfig };
}

export const useDataPortalStore = create<DataPortalState>((set) => ({
    ...hydrateFromCache(),
    isLoading: false,

    refresh: async () => {
        const userId = getUserInfo()?.account?.id;
        if (userId == null) return;
        const uid = String(userId);

        set({ isLoading: true });
        try {
            // 灰度与菜单**并行**：两个都是慢接口（实测灰度 ~1.8s、菜单 ~3.4s），串行等于两者相加。
            // 常见路径（灰度已开）直接省掉灰度那一段的串行等待；灰度关的罕见情况下 menu 结果丢弃即可，
            // 后端此时本就返回 EMPTY_MENU，不会有副作用。
            // 配置权限不走 batchGetConfigEnable —— 按门户分权后是 JSON 名单，那接口表达不了，由 menu 下发 canConfig
            const [configResult, menuResult] = await Promise.all([
                client.query<ConfigEnableResponse>({
                    query: BATCH_GET_CONFIG_ENABLE,
                    variables: { configKeys: [DATA_PORTAL_EMBED_ENABLE_KEY] },
                    fetchPolicy: 'network-only',
                }),
                client.query<MyMenuResponse>({
                    query: GET_MY_DATA_PORTAL_MENU,
                    fetchPolicy: 'network-only',
                }),
            ]);

            const results = configResult.data?.batchGetConfigEnable?.results ?? [];
            const enabled = results.some((r) => r.configKey === DATA_PORTAL_EMBED_ENABLE_KEY && r.enabled);
            saveDataPortalEnabled(uid, enabled);

            if (!enabled) {
                // 灰度关闭：丢弃 menu 结果，清空（配置入口也不该出现，与后端 canConfig=false 一致）
                saveDataPortalCanConfig(uid, false);
                saveMyDataPortalPages(uid, []);
                saveMyDataPortalGroups(uid, []);
                saveMyDataPortalPortalCodes(uid, []);
                set({
                    enabled: false,
                    canConfig: false,
                    pages: [],
                    groups: [],
                    portalCodes: [],
                    isLoading: false,
                });
                return;
            }

            const applied = applyMenu(uid, menuResult.data?.myDataPortalMenu);
            set({ enabled: true, ...applied, isLoading: false });
        } catch (e) {
            // 拉不到就维持现状（可能是 hydrate 出来的旧值），只结束 loading。
            // 不清空：网络抖动不该让已经显示出来的菜单突然消失。
            set({ isLoading: false });
            throw e;
        }
    },

    refreshMenu: async () => {
        const userId = getUserInfo()?.account?.id;
        if (userId == null) return;
        const uid = String(userId);
        try {
            const menuResult = await client.query<MyMenuResponse>({
                query: GET_MY_DATA_PORTAL_MENU,
                fetchPolicy: 'network-only',
            });
            // 不动 enabled/isLoading：轻量刷新只更新菜单内容，灰度状态由 refresh 负责
            set(applyMenu(uid, menuResult.data?.myDataPortalMenu));
        } catch (e) {
            // 轻量刷新失败不抛、不清空，维持现状即可
            console.warn('Refresh data portal menu failed', e);
        }
    },
}));

/**
 * 「数据门户」一级菜单是否展示。
 *
 * 灰度开启，且二级栏里至少有一项可见：
 * - 配置白名单内的人恒有「看板管理」这一项，所以只要灰度开着就能进；
 * - 其它人要至少有一个可见看板，否则不给一个点进去空空如也的入口。
 *
 * 与 DataPortalSecondMenu 的构造保持一致，改一处要改两处。
 */
export const useShouldShowDataPortalMenu = (): boolean =>
    useDataPortalStore((s) => s.enabled && (s.canConfig || s.pages.length > 0));
